import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, RotateCcw, User, GraduationCap, Sparkles } from 'lucide-react';
import axios from 'axios';
import './App.css';

import { StickyHeader } from './components/Header';
import { ResumeDropzone } from './components/ResumeDropzone';
import { ContactForm } from './components/ContactForm';
import { AnalysisLoading } from './components/AnalysisLoading';
import { CareerTile, CareerDetailDrawer, SkillAssessment, ProfileSnapshot, EarningGap, EarningGapAlert, CareerHealth, Verdict } from './components/CareerComponents';
import { CareerPaths } from './components/CareerPaths';
import { initTracking, trackLandingPageView } from './utils/analytics';
import LandingPage from './components/LandingPage';
import { EntrepreneurshipSection } from './components/EntrepreneurshipSection';
import { AlumniTestimonials } from './components/AlumniTestimonials';
import { AdminLogin } from './components/AdminLogin';
import { AdminDashboard } from './components/AdminDashboard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ─── HERO SECTION ─────────────────────────────────────────────────────────────
// No background needed here — the page-level gradient covers the top already

const HeroSection = ({ onStartClick }) => (
  <div style={{
    padding: '60px 24px 72px',
    textAlign: 'center',
    position: 'relative',
  }}>
    {/* Kicker pill */}
    <div style={{
      display: 'inline-flex',
      alignItems: 'center',
      backgroundColor: '#ffffff',
      border: '1px solid rgba(0,0,0,0.10)',
      borderRadius: '9999px',
      padding: '5px 14px',
      marginBottom: '24px',
      boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    }}>
      <span style={{
        fontFamily: 'DM Sans, sans-serif',
        fontSize: '10px',
        fontWeight: 500,
        color: 'rgba(0,0,0,0.45)',
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
      }}>
        Masters' Union · MBA Pre-Admissions
      </span>
    </div>

    {/* Headline */}
    <div style={{ marginBottom: '18px' }}>
      <h1 style={{
        fontFamily: 'DM Sans, sans-serif',
        fontSize: 'clamp(32px, 5vw, 58px)',
        fontWeight: 700,
        color: '#111111',
        letterSpacing: '-0.025em',
        lineHeight: '1.08',
        marginBottom: '4px',
      }}>
        See your{' '}
        <span style={{
          fontFamily: 'Cormorant Garamond, serif',
          fontStyle: 'italic',
          fontWeight: 500,
          color: '#E8A020',
        }}>
          next career move
        </span>
      </h1>
      <h1 style={{
        fontFamily: 'DM Sans, sans-serif',
        fontSize: 'clamp(32px, 5vw, 58px)',
        fontWeight: 700,
        color: '#111111',
        letterSpacing: '-0.025em',
        lineHeight: '1.08',
      }}>
        before you apply.
      </h1>
    </div>

    {/* Subheadline */}
    <p style={{
      fontFamily: 'DM Sans, sans-serif',
      fontSize: 'clamp(14px, 1.8vw, 17px)',
      color: 'rgba(0,0,0,0.52)',
      maxWidth: '560px',
      margin: '0 auto 36px',
      lineHeight: '1.65',
    }}>
      Upload your resume to get an AI-powered heat map of post-MBA career paths,
      expected CTC, and a step-by-step roadmap — personalised to your background.
    </p>

    {/* Trust chips */}
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexWrap: 'wrap',
      gap: '10px',
      marginBottom: '36px',
    }}>
      {[
        { label: '~60 seconds', sub: 'Takes only' },
        { label: '12 career paths', sub: 'Mapped for you' },
        { label: 'AI-powered', sub: 'GPT analysis' },
      ].map((item) => (
        <div key={item.label} style={{
          display: 'flex',
          alignItems: 'center',
          gap: '7px',
          padding: '7px 14px',
          backgroundColor: '#ffffff',
          border: '1px solid rgba(0,0,0,0.09)',
          borderRadius: '9999px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        }}>
          <span style={{
            fontFamily: 'DM Sans, sans-serif',
            fontSize: '14px',
            color: 'rgba(0,0,0,0.38)',
          }}>{item.sub}</span>
          <span style={{
            fontFamily: 'DM Sans, sans-serif',
            fontSize: '14.5px',
            fontWeight: 600,
            color: '#111111',
          }}>{item.label}</span>
        </div>
      ))}
    </div>

    {/* CTA — use Sparkles icon (works reliably) */}
    <button
      data-testid="hero-primary-cta"
      onClick={onStartClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        padding: '14px 28px',
        backgroundColor: '#E8A020',
        border: 'none',
        borderRadius: '9999px',
        cursor: 'pointer',
        fontFamily: 'DM Sans, sans-serif',
        fontSize: '14px',
        fontWeight: 600,
        color: '#ffffff',
        boxShadow: '0 2px 12px rgba(232,160,32,0.30)',
        transition: 'transform 0.15s ease, box-shadow 0.2s ease',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'scale(1.02)';
        e.currentTarget.style.boxShadow = '0 4px 20px rgba(232,160,32,0.40)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'scale(1)';
        e.currentTarget.style.boxShadow = '0 2px 12px rgba(232,160,32,0.30)';
      }}
    >
      <Sparkles size={16} />
      Analyse my resume
      <ArrowRight size={16} />
    </button>
  </div>
);

// ─── CANDIDATE SUMMARY CARD ──────────────────────────────────────────────────

const CandidateSummaryCard = ({ profile }) => (
  <motion.div
    data-testid="candidate-summary-card"
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.35 }}
    style={{
      backgroundColor: '#ffffff',
      border: '1px solid rgba(0,0,0,0.08)',
      borderRadius: '12px',
      padding: '18px 20px',
      marginBottom: '24px',
      boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
    }}
  >
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', flexWrap: 'wrap' }}>
      {/* Avatar */}
      <div style={{
        width: '44px',
        height: '44px',
        borderRadius: '50%',
        backgroundColor: 'rgba(232,160,32,0.10)',
        border: '1.5px solid rgba(232,160,32,0.28)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}>
        <User size={20} color="#E8A020" />
      </div>

      {/* Main info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap', marginBottom: '4px' }}>
          <h3 data-testid="candidate-summary-current-role" style={{
            fontFamily: 'DM Sans, sans-serif',
            fontSize: '14.5px',
            fontWeight: 700,
            color: '#111111',
          }}>
            {profile.name}
          </h3>
          <span style={{
            fontFamily: 'DM Sans, sans-serif',
            fontSize: '14.5px',
            color: 'rgba(0,0,0,0.40)',
          }}>
            {profile.years_experience} years experience
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px', flexWrap: 'wrap' }}>
          <GraduationCap size={12} color="rgba(0,0,0,0.35)" />
          <span style={{
            fontFamily: 'DM Sans, sans-serif',
            fontSize: '14.5px',
            color: 'rgba(0,0,0,0.50)',
          }}>
            {profile.education}
          </span>
        </div>

        {/* Skills */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {profile.key_skills?.slice(0, 5).map((skill, i) => (
            <span key={i} style={{
              fontFamily: 'DM Sans, sans-serif',
              fontSize: '14px',
              fontWeight: 500,
              color: 'rgba(0,0,0,0.60)',
              backgroundColor: '#f5f5f5',
              border: '1px solid rgba(0,0,0,0.08)',
              borderRadius: '9999px',
              padding: '3px 9px',
            }}>
              {skill}
            </span>
          ))}
        </div>
      </div>

      {/* Domains */}
      {profile.domains?.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', alignItems: 'flex-end' }}>
          {profile.domains.slice(0, 2).map((d, i) => (
            <span key={i} style={{
              fontFamily: 'DM Sans, sans-serif',
              fontSize: '14px',
              color: '#C47A00',
              backgroundColor: 'rgba(232,160,32,0.10)',
              border: '1px solid rgba(232,160,32,0.22)',
              borderRadius: '9999px',
              padding: '3px 9px',
              fontWeight: 500,
            }}>
              {d}
            </span>
          ))}
        </div>
      )}
    </div>

    {/* Highlights */}
    {profile.highlights?.length > 0 && (
      <div style={{ marginTop: '14px', paddingTop: '14px', borderTop: '1px solid rgba(0,0,0,0.06)' }}>
        <div style={{ display: 'flex', gap: '18px', flexWrap: 'wrap' }}>
          {profile.highlights.slice(0, 3).map((h, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ width: '4px', height: '4px', borderRadius: '50%', backgroundColor: '#3FA86A', flexShrink: 0 }} />
              <span style={{
                fontFamily: 'DM Sans, sans-serif',
                fontSize: '14.5px',
                color: 'rgba(0,0,0,0.55)',
              }}>{h}</span>
            </div>
          ))}
        </div>
      </div>
    )}
  </motion.div>
);

// ─── RESULTS SECTION ─────────────────────────────────────────────────────────

const ResultsSection = ({ analysis, onRegenerate }) => {
  const { candidate_profile } = analysis;
  const firstName = candidate_profile?.name?.split(' ')[0] || 'Your';

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      {/* Results header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: '20px', flexWrap: 'wrap', gap: '12px',
      }}>
        <div>
          <h2 style={{
            fontFamily: 'DM Sans, sans-serif', fontSize: '22px',
            fontWeight: 700, color: '#111111', marginBottom: '4px',
          }}>
            {firstName}'s Career{' '}
            <span style={{
              fontFamily: 'Cormorant Garamond, serif',
              fontStyle: 'italic', fontWeight: 500, color: '#E8A020',
            }}>
              Snapshot
            </span>
          </h2>
          <p style={{
            fontFamily: 'DM Sans, sans-serif', fontSize: '14px',
            color: 'rgba(0,0,0,0.44)',
          }}>
            A clear read on your current position, earning upside, and where your profile could realistically go next.
          </p>
        </div>
      </div>

      {/* ── 1. Alert banner ── */}
      {analysis.earnings_gap?.is_underpaid && (
        <EarningGapAlert earningsGap={analysis.earnings_gap} />
      )}

      {/* ── 2. Career Health (Key Findings) — right after alert ── */}
      {analysis.career_health?.working_for_you && (
        <CareerHealth
          careerHealth={analysis.career_health}
          earningPotentialMax={analysis.earnings_gap?.post_mba_max}
        />
      )}

      {/* ── 3. Earning Potential Gap (main card, no alert) ── */}
      {analysis.earnings_gap?.verdict && (
        <EarningGap earningsGap={analysis.earnings_gap} />
      )}

      {/* ── Competency Benchmark — current role, core competencies ── */}
      {analysis.skill_assessment && (
        Array.isArray(analysis.skill_assessment)
          ? analysis.skill_assessment.length > 0
          : analysis.skill_assessment?.competencies?.length > 0
      ) && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.1 }}
          style={{
            backgroundColor: '#ffffff',
            border: '1px solid rgba(0,0,0,0.08)',
            borderRadius: '12px',
            padding: '22px 24px',
            marginBottom: '24px',
            boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
          }}
        >
          <div style={{ marginBottom: '18px' }}>
            <h3 style={{
              fontFamily: 'DM Sans, sans-serif', fontSize: '22px',
              fontWeight: 700, color: '#111111', marginBottom: '6px',
              letterSpacing: '-0.01em',
            }}>
              Competency{' '}
              <span style={{
                fontFamily: 'Cormorant Garamond, serif',
                fontStyle: 'italic',
                fontWeight: 500,
                color: '#E8A020',
              }}>
                Benchmark
              </span>
            </h3>
            <p style={{
              fontFamily: 'DM Sans, sans-serif', fontSize: '14px',
              color: 'rgba(0,0,0,0.60)', lineHeight: '1.6',
              marginBottom: '6px',
            }}>
              A sharper view of where your profile already stands out — and where it still needs depth to excel in your career trajectory.
            </p>
            <p style={{
              fontFamily: 'DM Sans, sans-serif',
              fontStyle: 'italic',
              fontSize: '14.5px',
              color: 'rgba(0,0,0,0.38)',
              lineHeight: '1.55',
              margin: 0,
            }}>
              These scores are inferred from your resume, role scope, ownership signals, and benchmarked against the top 1% for your role.
            </p>
          </div>
          <SkillAssessment
            skillAssessment={analysis.skill_assessment}
            profileSnapshot={analysis.profile_snapshot}
          />
        </motion.div>
      )}

      {/* ── Career Paths — 6 rich role cards ── */}
      {analysis.career_paths_v2?.length > 0 && (
        <CareerPaths careerPaths={analysis.career_paths_v2} />
      )}

      {/* ── Alumni Testimonials — just below career paths ── */}
      <AlumniTestimonials
        yearsExperience={analysis.candidate_profile?.years_experience}
      />

      {/* ── Verdict — CTA section ── */}
      {analysis.verdict?.headline && (
        <Verdict
          verdict={analysis.verdict}
          candidateName={candidate_profile?.name}
          analysisId={analysis.id}
        />
      )}

      {/* ── Entrepreneurship ── */}
      <EntrepreneurshipSection founderFitment={analysis.founder_fitment} />

    </motion.div>
  );
};

// ─── WIZARD CARD ─────────────────────────────────────────────────────────────

const WizardCard = ({ step, onUploadComplete, onContactSubmit, onContactBack, isSubmitting }) => (
  <div
    data-testid="wizard-card"
    style={{
      backgroundColor: '#ffffff',
      border: '1px solid rgba(0,0,0,0.08)',
      borderRadius: '16px',
      padding: '28px',
      width: '100%',
      maxWidth: '480px',
      boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
    }}
  >
    <AnimatePresence mode="wait">
      {step === 'upload' && (
        <motion.div
          key="upload"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.22 }}
        >
          <div style={{ marginBottom: '20px', textAlign: 'center' }}>
            <h3 style={{
              fontFamily: 'DM Sans, sans-serif',
              fontSize: '20px',
              fontWeight: 700,
              color: '#111111',
              marginBottom: '8px',
            }}>Upload your resume</h3>
            <p style={{
              fontFamily: 'DM Sans, sans-serif',
              fontSize: '14px',
              color: 'rgba(0,0,0,0.44)',
              lineHeight: '1.6',
              maxWidth: '360px',
              margin: '0 auto',
            }}>
              We'll read your experience, education, skills, and achievements to plan your next serious move.
            </p>
          </div>
          <ResumeDropzone onUploadComplete={onUploadComplete} />
        </motion.div>
      )}
      {step === 'contact' && (
        <motion.div
          key="contact"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.22 }}
        >
          <ContactForm
            onSubmit={onContactSubmit}
            onBack={onContactBack}
            isLoading={isSubmitting}
          />
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

// ─── MAIN APP ─────────────────────────────────────────────────────────────────

function App() {
  // ── All hooks must be called before any early returns ─────────────────────
  const [appState, setAppState] = useState('landing');
  const [submissionData, setSubmissionData] = useState(null);
  const [analysisId, setAnalysisId] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [shareCopied, setShareCopied] = useState(false);

  // ── Init analytics once on mount ─────────────────────────────────────────
  useEffect(() => { initTracking(); }, []);

  // ── Track landing page view when user sees it ─────────────────────────────
  useEffect(() => {
    if (appState === 'landing') trackLandingPageView();
  }, [appState]);

  // ── Admin routing (after hooks) ──────────────────────────────────────────
  const pathname = window.location.pathname;
  const isAdmin = pathname === '/admin' || pathname === '/admin/' || pathname.startsWith('/admin/');
  const isAdminDashboard = pathname.startsWith('/admin/');

  // On mount: check for ?report= param (shared link)
  useEffect(() => {
    if (isAdmin) return; // don't run for admin pages
    const params = new URLSearchParams(window.location.search);
    const reportId = params.get('report');
    if (reportId) {
      setAnalysisId(reportId);
      setAppState('analyzing');
    }
  }, [isAdmin]);

  // Update URL when results are available
  useEffect(() => {
    if (isAdmin) return;
    if (appState === 'results' && analysisId) {
      window.history.replaceState(null, '', `${window.location.pathname}?report=${analysisId}`);
    } else if (appState === 'upload' || appState === 'landing') {
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, [appState, analysisId, isAdmin]);

  // Poll for analysis completion
  useEffect(() => {
    if (isAdmin || !analysisId || appState !== 'analyzing') return;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/analysis/${analysisId}`);
        const data = res.data;
        if (data.status === 'completed') { setAnalysisResult(data); setAppState('results'); }
        else if (data.status === 'failed') { setError(data.error || 'Analysis failed. Please try again.'); setAppState('upload'); }
      } catch (err) { console.error('Poll error:', err); }
    };
    const interval = setInterval(poll, 3000);
    poll();
    return () => clearInterval(interval);
  }, [analysisId, appState, isAdmin]);

  const handleShare = useCallback(() => {
    const shareUrl = `${window.location.origin}${window.location.pathname}?report=${analysisId}`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(shareUrl).then(() => { setShareCopied(true); setTimeout(() => setShareCopied(false), 2500); });
    } else {
      const el = document.createElement('textarea');
      el.value = shareUrl; document.body.appendChild(el); el.select();
      document.execCommand('copy'); document.body.removeChild(el);
      setShareCopied(true); setTimeout(() => setShareCopied(false), 2500);
    }
  }, [analysisId]);

  const handleUploadComplete = useCallback((data) => { setSubmissionData(data); setAppState('contact'); }, []);

  const handleContactSubmit = useCallback(async ({ email, phone, current_ctc }) => {
    if (!submissionData?.submission_id) return;
    setIsSubmitting(true); setError('');
    try {
      const res = await axios.post(`${API}/analyze`, {
        email, phone, current_ctc: current_ctc || '',
        submission_id: submissionData.submission_id,
      });
      setAnalysisId(res.data.analysis_id); setAppState('analyzing');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to start analysis. Please try again.');
    } finally { setIsSubmitting(false); }
  }, [submissionData]);

  const handleRegenerate = useCallback(async () => {
    if (!analysisId) return;
    setError('');
    try {
      const res = await axios.post(`${API}/reanalyze/${analysisId}`);
      setAnalysisId(res.data.analysis_id);
      setAnalysisResult(null);
      setAppState('analyzing');
    } catch (err) {
      setError(err.response?.data?.detail || 'Regeneration failed. Please try again.');
    }
  }, [analysisId]);

  const handleReset = useCallback(() => {
    setAppState('upload'); setSubmissionData(null);
    setAnalysisId(null); setAnalysisResult(null); setError('');
  }, []);

  const handleHeroCTA = useCallback(() => {
    setAppState('upload');
    setTimeout(() => {
      const el = document.getElementById('wizard-section');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  }, []);

  // ── Admin early returns ───────────────────────────────────────────────────
  if (isAdmin && isAdminDashboard) return <AdminDashboard />;
  if (isAdmin) return <AdminLogin />;

  const showWizard = appState === 'upload' || appState === 'contact';
  const showLoading = appState === 'analyzing';
  const showResults = appState === 'results';

  const currentStep =
    appState === 'contact'   ? 'contact'  :
    appState === 'analyzing' ? 'analyzing':
    appState === 'results'   ? 'results'  : 'upload';

  // ── Page-level background: warm cream gradient on hero states ─────────────
  const pageBackground =
    appState === 'landing' || appState === 'upload' || appState === 'contact'
      ? 'radial-gradient(120% 40% at 50% 0%, rgb(255, 247, 230) 0%, rgb(253, 250, 244) 32%, rgb(250, 250, 250) 60%)'
      : '#fafafa';

  return (
    <div
      data-testid="page-root"
      className="App"
      style={{
        minHeight: '100vh',
        background: pageBackground,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Sticky floating header */}
      <StickyHeader
        step={currentStep}
        onShare={appState === 'results' ? handleShare : null}
        shareCopied={shareCopied}
        isLanding={appState === 'landing'}
      />

      {/* Spacer so content doesn't start under the fixed header */}
      {appState !== 'landing' && (
        <div style={{ height: '72px', flexShrink: 0 }} />
      )}

      {/* Hero (landing only) */}
      {appState === 'landing' && <LandingPage onGetStarted={handleHeroCTA} />}

      {/* Main content — flex:1 pushes footer to bottom */}
      <div style={{
        flex: '1 0 auto',
        maxWidth: '1100px',
        width: '100%',
        margin: '0 auto',
        padding: appState === 'results' ? '32px 24px 64px' : '48px 24px 64px',
        boxSizing: 'border-box',
      }}>

        {/* Error banner */}
        {error && (
          <div style={{
            backgroundColor: 'rgba(217,64,64,0.06)',
            border: '1px solid rgba(217,64,64,0.20)',
            borderRadius: '10px',
            padding: '12px 16px',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14.5px', color: '#D94040' }}>
              ⚠ {error}
            </span>
          </div>
        )}

        {/* WIZARD */}
        {showWizard && (
          <div
            id="wizard-section"
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '24px' }}
          >
            <div style={{ textAlign: 'center', maxWidth: '420px' }}>
              {appState === 'upload' && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <h2 style={{
                    fontFamily: 'DM Sans, sans-serif',
                    fontSize: 'clamp(22px, 3vw, 28px)',
                    fontWeight: 700,
                    color: '#111111',
                    marginBottom: '10px',
                    letterSpacing: '-0.02em',
                    lineHeight: '1.2',
                  }}>
                    Let's analyze what your<br />profile is capable of
                  </h2>
                  <p style={{
                    fontFamily: 'DM Sans, sans-serif',
                    fontSize: '14.5px',
                    color: 'rgba(0,0,0,0.44)',
                    lineHeight: '1.6',
                  }}>
                    Upload your resume to get a personalized analysis of your career trajectory, earning potential, and opportunities.
                  </p>
                </motion.div>
              )}
            </div>

            <WizardCard
              step={appState}
              onUploadComplete={handleUploadComplete}
              onContactSubmit={handleContactSubmit}
              onContactBack={() => setAppState('upload')}
              isSubmitting={isSubmitting}
            />

            {/* Trust signals */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '20px',
              flexWrap: 'wrap',
              justifyContent: 'center',
            }}>
              {['Your data stays private', 'No account needed', 'AI-powered analysis in ~60s'].map(label => (
                <span key={label} style={{
                  fontFamily: 'DM Sans, sans-serif',
                  fontSize: '14.5px',
                  color: 'rgba(0,0,0,0.50)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '7px',
                }}>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: '17px', height: '17px', borderRadius: '50%', flexShrink: 0,
                    backgroundColor: '#3FA86A',
                    color: '#ffffff', fontSize: '9px', fontWeight: 700,
                  }}>✓</span>
                  {label}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* ANALYZING */}
        {showLoading && (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <div style={{
              backgroundColor: '#ffffff',
              border: '1px solid rgba(0,0,0,0.08)',
              borderRadius: '16px',
              padding: '32px 28px',
              width: '100%',
              maxWidth: '480px',
              boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
            }}>
              <AnalysisLoading />
            </div>
          </div>
        )}

        {/* RESULTS */}
        {showResults && analysisResult && (
          <ResultsSection analysis={analysisResult} onRegenerate={handleRegenerate} />
        )}
      </div>

      {/* Footer — always at bottom thanks to flex column layout */}
      <footer style={{
        flexShrink: 0,
        borderTop: '1px solid rgba(0,0,0,0.07)',
        padding: '20px 24px',
        textAlign: 'center',
        backgroundColor: '#ffffff',
      }}>
        <p style={{
          fontFamily: 'DM Sans, sans-serif',
          fontSize: '14.5px',
          color: 'rgba(0,0,0,0.28)',
        }}>
          © 2026 Masters' Union | Career analysis powered with AI
        </p>
      </footer>
    </div>
  );
}

export default App;
